// ./src/hooks/useServiceIntervalNexts.ts
import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { OpResult, OP_RESULT_CODES } from "@sdflc/api-helpers";
import {
  ServiceIntervalNext,
  ServiceIntervalNextService,
  ServiceIntervalNextFilter,
  ServiceIntervalNextInput,
  UrgencyStatus,
  Pagination,
  SortItem,
} from "@/utils/api";
import { ApiError } from "@/utils";
import { DEFAULT_PAGE_SIZE } from "@/config";

// ============================================
// Types
// ============================================

export interface UseServiceIntervalNextsOptions {
  /** Initial page size for pagination */
  pageSize?: number;

  /** Initial filters */
  initialFilters?: Partial<ServiceIntervalNextFilter>;

  /** Initial sort configuration */
  initialSort?: SortItem[];

  /** Auto-load data on mount */
  autoLoad?: boolean;

  /** Current user ID - data reloads when this changes */
  userId?: string | null;

  /** Car ID to filter by (optional - if not provided, shows all cars) */
  carId?: string | null;

  /** Urgency status filter */
  urgencyStatus?: UrgencyStatus[] | null;
}

export interface UseServiceIntervalNextsReturn {
  // Data state
  serviceIntervalNexts: ServiceIntervalNext[];
  loading: boolean;
  isEmpty: boolean;

  // Search and filters
  filters: Partial<ServiceIntervalNextFilter>;
  sortBy: SortItem[];

  // Operations state
  isUpdating: boolean;
  updatingId: string | null;
  selectedServiceIntervalNext: ServiceIntervalNext | null;

  // Core operations - return OpResult for immediate access to result
  list: (
    filter?: ServiceIntervalNextFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ) => Promise<OpResult>;
  get: (id: string) => Promise<OpResult>;
  update: (id: string, params: ServiceIntervalNextInput) => Promise<OpResult>;

  // Additional convenience methods
  refresh: () => Promise<OpResult>;

  // UI state management
  setSelectedServiceIntervalNext: (service: ServiceIntervalNext | null) => void;

  // Helper operations
  reset: () => void;

  // Filter helpers
  setCarIdFilter: (carId: string | null) => void;
  setUrgencyStatusFilter: (status: UrgencyStatus[] | null) => void;

  // Lookup helpers
  getServiceIntervalNextById: (
    id: string | undefined
  ) => ServiceIntervalNext | undefined;
  getServicesByCarId: (carId: string) => ServiceIntervalNext[];
  getServicesByUrgency: (status: UrgencyStatus) => ServiceIntervalNext[];
  getOverdueServices: () => ServiceIntervalNext[];
  getDueSoonServices: () => ServiceIntervalNext[];

  // Error handling - standardized ApiError with built-in checkers
  lastError: ApiError | null;
  clearError: () => void;
}

// ============================================
// Constants
// ============================================

const DEFAULT_SORT: SortItem[] = [{ name: "nextWhenDo", order: "ASC" }];

// Urgency priority for client-side sorting
const URGENCY_PRIORITY: Record<UrgencyStatus, number> = {
  overdue: 0,
  due_soon: 1,
  upcoming: 2,
  ok: 3,
};

// ============================================
// Hook
// ============================================

export const useServiceIntervalNexts = (
  options: UseServiceIntervalNextsOptions = {}
): UseServiceIntervalNextsReturn => {
  const {
    pageSize = DEFAULT_PAGE_SIZE,
    initialFilters = {},
    initialSort = DEFAULT_SORT,
    autoLoad = false,
    userId = null,
    carId = null,
    urgencyStatus = null,
  } = options;

  // Use refs for option values to avoid dependency issues
  const pageSizeRef = useRef(pageSize);
  const sortByRef = useRef<SortItem[]>(initialSort);
  const initialFiltersRef = useRef(initialFilters);
  const previousUserIdRef = useRef<string | null | undefined>(undefined);
  const urgencyStatusRef = useRef<UrgencyStatus[] | null>(urgencyStatus);

  // Update refs when options change
  useEffect(() => {
    pageSizeRef.current = pageSize;
  }, [pageSize]);

  useEffect(() => {
    initialFiltersRef.current = initialFilters;
  }, [initialFilters]);

  // Update urgencyStatus ref only when values actually change (not just reference)
  useEffect(() => {
    const prevJson = JSON.stringify(urgencyStatusRef.current);
    const newJson = JSON.stringify(urgencyStatus);
    if (prevJson !== newJson) {
      urgencyStatusRef.current = urgencyStatus;
    }
  }, [urgencyStatus]);

  // Data state
  const [serviceIntervalNexts, setServiceIntervalNexts] = useState<
    ServiceIntervalNext[]
  >([]);
  const [loading, setLoading] = useState(false);

  // Search and filters
  const [filters, setFilters] =
    useState<Partial<ServiceIntervalNextFilter>>(initialFilters);
  const [sortBy, setSortBy] = useState<SortItem[]>(initialSort);

  // Update sortByRef when sortBy state changes
  useEffect(() => {
    sortByRef.current = sortBy;
  }, [sortBy]);

  // Operations state
  const [isUpdating, setIsUpdating] = useState(false);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [selectedServiceIntervalNext, setSelectedServiceIntervalNext] =
    useState<ServiceIntervalNext | null>(null);

  // Error handling - now uses standardized ApiError
  const [lastError, setLastError] = useState<ApiError | null>(null);

  // Computed values
  const isEmpty = useMemo(
    () => serviceIntervalNexts.length === 0 && !loading,
    [serviceIntervalNexts.length, loading]
  );

  // ============================================
  // Helper Functions
  // ============================================

  // Sort services by urgency priority, then by nextWhenDo
  const sortByUrgency = useCallback(
    (services: ServiceIntervalNext[]): ServiceIntervalNext[] => {
      return [...services].sort((a, b) => {
        // First sort by urgency priority
        const priorityA = URGENCY_PRIORITY[a.urgencyStatus || "ok"];
        const priorityB = URGENCY_PRIORITY[b.urgencyStatus || "ok"];

        if (priorityA !== priorityB) {
          return priorityA - priorityB;
        }

        // Then sort by nextWhenDo (earliest first)
        const dateA = a.nextWhenDo
          ? new Date(a.nextWhenDo).getTime()
          : Infinity;
        const dateB = b.nextWhenDo
          ? new Date(b.nextWhenDo).getTime()
          : Infinity;

        if (dateA !== dateB) {
          return dateA - dateB;
        }

        // Finally sort by nextOdometer
        const odometerA = a.nextOdometer ?? Infinity;
        const odometerB = b.nextOdometer ?? Infinity;

        return odometerA - odometerB;
      });
    },
    []
  );

  // Update item in list by ID
  const updateInListById = useCallback(
    (updatedItem: ServiceIntervalNext) => {
      setServiceIntervalNexts((prev) => {
        const existingIndex = prev.findIndex(
          (item) => item.id === updatedItem.id
        );
        if (existingIndex >= 0) {
          const newItems = [...prev];
          newItems[existingIndex] = updatedItem;
          return sortByUrgency(newItems);
        }
        return prev;
      });
    },
    [sortByUrgency]
  );

  // Clear error function
  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  // Reset function
  const reset = useCallback(() => {
    setServiceIntervalNexts([]);
    setLastError(null);
    setLoading(false);
    setIsUpdating(false);
    setUpdatingId(null);
    setSelectedServiceIntervalNext(null);
  }, []);

  // ============================================
  // Core Operations
  // ============================================

  // List service interval nexts
  // Returns OpResult for immediate access to result
  const list = useCallback(
    async (
      filter?: ServiceIntervalNextFilter,
      pagination?: Pagination,
      sortByParams?: SortItem[]
    ): Promise<OpResult> => {
      setLoading(true);
      if (filter) setFilters(filter);
      if (sortByParams) setSortBy(sortByParams);
      setLastError(null);

      const currentSortBy = sortByParams || sortByRef.current;

      try {
        const paginationParams: Pagination | undefined = pagination
          ? {
            page: pagination.page,
            pageSize: pagination.pageSize,
          }
          : undefined;

        const sortParams = currentSortBy.map((sort) => sort);

        const result = await ServiceIntervalNextService.list(
          filter,
          paginationParams,
          sortParams
        );

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const items = Array.isArray(result.data)
            ? (result.data as ServiceIntervalNext[])
            : [];
          // Sort by urgency on client side for better UX
          setServiceIntervalNexts(sortByUrgency(items));
          setLastError(null);
        } else {
          setLastError(
            ApiError.fromResult(result, "Failed to load upcoming services")
          );
          setServiceIntervalNexts([]);
        }

        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(
          ApiError.fromException(error, "Failed to load upcoming services")
        );
        setServiceIntervalNexts([]);
        return result;
      } finally {
        setLoading(false);
      }
    },
    [sortByUrgency]
  );

  // Get single service interval next
  // Returns OpResult for immediate access to result
  const get = useCallback(async (id: string): Promise<OpResult> => {
    setLastError(null);

    try {
      const result = await ServiceIntervalNextService.get(id);

      if (result.code === OP_RESULT_CODES.OK && result.data) {
        // Data is available in result for caller to use
        setLastError(null);
      } else {
        setLastError(
          ApiError.fromResult(result, "Failed to get service interval")
        );
      }

      return result;
    } catch (error) {
      const result = OpResult.fromException(error);
      setLastError(
        ApiError.fromException(error, "Failed to get service interval")
      );
      return result;
    }
  }, []);

  // Update service interval next (user override)
  // Returns OpResult for immediate access to result
  const update = useCallback(
    async (id: string, params: ServiceIntervalNextInput): Promise<OpResult> => {
      setIsUpdating(true);
      setUpdatingId(id);
      setLastError(null);

      try {
        const result = await ServiceIntervalNextService.update(id, params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const updatedItem = (result.data as ServiceIntervalNext[])[0];
          if (updatedItem) {
            updateInListById(updatedItem);
          }
          setLastError(null);
        } else {
          setLastError(
            ApiError.fromResult(result, "Failed to update service interval")
          );
        }

        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(
          ApiError.fromException(error, "Failed to update service interval")
        );
        return result;
      } finally {
        setIsUpdating(false);
        setUpdatingId(null);
      }
    },
    [updateInListById]
  );

  // Refresh with current filters
  // Returns OpResult for immediate access to result
  const refresh = useCallback(async (): Promise<OpResult> => {
    const currentFilter: ServiceIntervalNextFilter = {
      ...initialFiltersRef.current,
    };

    // Apply carId filter if set
    if (carId) {
      currentFilter.carId = [carId];
    }

    // Apply urgency status filter if set
    const currentUrgencyStatus = urgencyStatusRef.current;
    if (currentUrgencyStatus && currentUrgencyStatus.length > 0) {
      currentFilter.urgencyStatus = currentUrgencyStatus;
    }

    return list(currentFilter, undefined, sortByRef.current);
  }, [list, carId]);

  // ============================================
  // Filter Helpers
  // ============================================

  const setCarIdFilter = useCallback((newCarId: string | null) => {
    setFilters((prev) => ({
      ...prev,
      carId: newCarId ? [newCarId] : null,
    }));
  }, []);

  const setUrgencyStatusFilter = useCallback(
    (status: UrgencyStatus[] | null) => {
      setFilters((prev) => ({
        ...prev,
        urgencyStatus: status,
      }));
    },
    []
  );

  // ============================================
  // Lookup Helpers
  // ============================================

  const getServiceIntervalNextById = useCallback(
    (id: string | undefined): ServiceIntervalNext | undefined => {
      if (!id) return undefined;
      return serviceIntervalNexts.find((item) => item.id === id);
    },
    [serviceIntervalNexts]
  );

  const getServicesByCarId = useCallback(
    (targetCarId: string): ServiceIntervalNext[] => {
      return serviceIntervalNexts.filter((item) => item.carId === targetCarId);
    },
    [serviceIntervalNexts]
  );

  const getServicesByUrgency = useCallback(
    (status: UrgencyStatus): ServiceIntervalNext[] => {
      return serviceIntervalNexts.filter(
        (item) => item.urgencyStatus === status
      );
    },
    [serviceIntervalNexts]
  );

  const getOverdueServices = useCallback((): ServiceIntervalNext[] => {
    return serviceIntervalNexts.filter(
      (item) => item.urgencyStatus === "overdue"
    );
  }, [serviceIntervalNexts]);

  const getDueSoonServices = useCallback((): ServiceIntervalNext[] => {
    return serviceIntervalNexts.filter(
      (item) => item.urgencyStatus === "due_soon"
    );
  }, [serviceIntervalNexts]);

  // ============================================
  // Auto-load and User Change Effects
  // ============================================

  useEffect(() => {
    const previousUserId = previousUserIdRef.current;
    previousUserIdRef.current = userId;

    // Skip on initial mount (when previousUserId is undefined)
    if (previousUserId === undefined) {
      // Initial load if autoLoad is enabled and user is logged in
      if (autoLoad && userId) {
        const initialFilter: ServiceIntervalNextFilter = {
          ...initialFiltersRef.current,
        };

        if (carId) {
          initialFilter.carId = [carId];
        }

        const currentUrgencyStatus = urgencyStatusRef.current;
        if (currentUrgencyStatus && currentUrgencyStatus.length > 0) {
          initialFilter.urgencyStatus = currentUrgencyStatus;
        }

        list(initialFilter, undefined, sortByRef.current);
      }
      return;
    }

    // User changed (including sign out)
    if (previousUserId !== userId) {
      // Always reset when user changes
      reset();

      // If new user is logged in and autoLoad is enabled, load their data
      if (autoLoad && userId) {
        const initialFilter: ServiceIntervalNextFilter = {
          ...initialFiltersRef.current,
        };

        if (carId) {
          initialFilter.carId = [carId];
        }

        const currentUrgencyStatus = urgencyStatusRef.current;
        if (currentUrgencyStatus && currentUrgencyStatus.length > 0) {
          initialFilter.urgencyStatus = currentUrgencyStatus;
        }

        list(initialFilter, undefined, sortByRef.current);
      }
    }
  }, [userId, autoLoad, carId, list, reset]); // removed urgencyStatus - using ref

  // Reload when carId filter changes
  // Note: urgencyStatus changes are handled via ref comparison in the effect
  useEffect(() => {
    // Only reload if we have a userId and autoLoad is enabled
    if (!autoLoad || !userId) return;

    // Skip initial load (handled by the user change effect)
    if (previousUserIdRef.current === undefined) return;

    const newFilter: ServiceIntervalNextFilter = {
      ...initialFiltersRef.current,
    };

    if (carId) {
      newFilter.carId = [carId];
    }

    const currentUrgencyStatus = urgencyStatusRef.current;
    if (currentUrgencyStatus && currentUrgencyStatus.length > 0) {
      newFilter.urgencyStatus = currentUrgencyStatus;
    }

    list(newFilter, undefined, sortByRef.current);
  }, [carId, autoLoad, userId, list]); // eslint-disable-line react-hooks/exhaustive-deps

  // ============================================
  // Return
  // ============================================

  return useMemo(
    () => ({
      // Data state
      serviceIntervalNexts,
      loading,
      isEmpty,

      // Search and filters
      filters,
      sortBy,

      // Operations state
      isUpdating,
      updatingId,
      selectedServiceIntervalNext,

      // Core operations
      list,
      get,
      update,

      // Convenience methods
      refresh,

      // UI state management
      setSelectedServiceIntervalNext,

      // Helper operations
      reset,

      // Filter helpers
      setCarIdFilter,
      setUrgencyStatusFilter,

      // Lookup helpers
      getServiceIntervalNextById,
      getServicesByCarId,
      getServicesByUrgency,
      getOverdueServices,
      getDueSoonServices,

      // Error handling
      lastError,
      clearError,
    }),
    [
      serviceIntervalNexts,
      loading,
      isEmpty,
      filters,
      sortBy,
      isUpdating,
      updatingId,
      selectedServiceIntervalNext,
      list,
      get,
      update,
      refresh,
      setCarIdFilter,
      setUrgencyStatusFilter,
      getServiceIntervalNextById,
      getServicesByCarId,
      getServicesByUrgency,
      getOverdueServices,
      getDueSoonServices,
      lastError,
      clearError,
      reset,
    ]
  );
};